import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import type { StoreGroup } from "../../Objects/StoreGroups/StoreGroup"
import { DeleteRequest, PostFormRequest, PostRequest, PutFormRequest } from "../../services/ApiClientService"

export type StoreGroupSaveRequest = {
  name: string
  description: string
  slug: string
  isActive?: boolean
  icon?: File | Blob | null
}

export const useSaveStoreGroup = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const handleError = (error: unknown) => {
    const message = error instanceof Error ? error.message : t("label_unknown_error_description")
    setError(t("label_title_erro"), message)
  }

  const handleCreateStoreGroup = async (request: StoreGroupSaveRequest): Promise<boolean> => {
    try {
      enableLoad()

      const response = await PostFormRequest<StoreGroup>(API_ROUTES.STORE_GROUPS.CREATE, {
        name: request.name,
        description: request.description,
        slug: request.slug,
        Icon: request.icon || undefined,
      })

      if (response.success) {
        return true
      }

      throw new Error(response.message)
    } catch (error) {
      handleError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  const handleUpdateStoreGroup = async (
    groupId: number,
    request: StoreGroupSaveRequest & { isActive: boolean },
  ): Promise<boolean> => {
    try {
      enableLoad()

      const response = await PutFormRequest<boolean>(API_ROUTES.STORE_GROUPS.UPDATE(groupId), {
        name: request.name,
        description: request.description,
        slug: request.slug,
        isActive: request.isActive,
        Icon: request.icon || undefined,
      })

      if (response.success) {
        return true
      }

      throw new Error(response.message)
    } catch (error) {
      handleError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  const handleInviteStore = async (groupId: number, storeSlug: string): Promise<boolean> => {
    try {
      enableLoad()

      const response = await PostRequest<boolean>(API_ROUTES.STORE_GROUPS.INVITE(groupId), { storeSlug })

      if (response.success) {
        return true
      }

      throw new Error(response.message)
    } catch (error) {
      handleError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  const handleDeleteStoreGroup = async (groupId: number): Promise<boolean> => {
    try {
      enableLoad()

      const response = await DeleteRequest<boolean>(API_ROUTES.STORE_GROUPS.DELETE(groupId))

      if (response.success) {
        return true
      }

      throw new Error(response.message)
    } catch (error) {
      handleError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  const handleAcceptInvite = async (memberId: number): Promise<boolean> => {
    try {
      enableLoad()

      const response = await PostRequest<boolean>(API_ROUTES.STORE_GROUPS.ACCEPT_INVITE(memberId), {})

      if (response.success) {
        return true
      }

      throw new Error(response.message)
    } catch (error) {
      handleError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  const handleRejectInvite = async (memberId: number): Promise<boolean> => {
    try {
      enableLoad()

      const response = await PostRequest<boolean>(API_ROUTES.STORE_GROUPS.REJECT_INVITE(memberId), {})

      if (response.success) {
        return true
      }

      throw new Error(response.message)
    } catch (error) {
      handleError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  const handleRevokeMember = async (memberId: number): Promise<boolean> => {
    try {
      enableLoad()

      const response = await PostRequest<boolean>(API_ROUTES.STORE_GROUPS.REVOKE_MEMBER(memberId), {})

      if (response.success) {
        return true
      }

      throw new Error(response.message)
    } catch (error) {
      handleError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  return {
    handleCreateStoreGroup,
    handleUpdateStoreGroup,
    handleDeleteStoreGroup,
    handleInviteStore,
    handleAcceptInvite,
    handleRejectInvite,
    handleRevokeMember,
  }
}
